import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { SITELINKS } from "./links";
import { ReactNode } from "react";

// view-pages
import SignIn from "../pages/auth/SignIn";
import AdminVerify from "../pages/auth/AdminVerify";
import ForgotPassword from "../pages/auth/ForgotPassword";
import ResetPassword from "../pages/auth/ResetPassword";
import Broadcast from "../pages/broadcast/Broadcast";
import Dashboard from "../pages/dashboard/Dashboard";
import Subscriptions from "../pages/subscription/Subscriptions";
import User from "../pages/user/Users";
import ShowUser from "../pages/user/ShowUser";
import useAuth from "./useAuth";
import Preloader from "../app-components/preloader/Preloader";
import CreateProfile from "../pages/user/CreateUser";
import UpdateUser from "../pages/user/UpdateUser";
import Transactions from "../pages/transaction/Transactions";
import Request from "../pages/request/Request";
import Responses from "../pages/request/Responses";
import EliteUser from "../pages/user/EliteUsers";
import ComingSoon from "../pages/coming-soon/ComingSoon";

// Middleware to validate token
function ProtectedAdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  // Render a loading indicator until authentication status is determined
  if (loading)
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );

  // If authenticated, render the children components
  return isAuthenticated ? (
    <>{children}</>
  ) : (
    // If not authenticated, redirect to the SignIn page
    <Navigate to={SITELINKS.SIGNIN} replace />
  );
}

// config routes
function APPRoutes() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<SignIn />} />
        <Route path={SITELINKS.SIGNIN} element={<SignIn />} />
        <Route path={SITELINKS.VERIFY_SIGNIN} element={<AdminVerify />} />
        <Route path={SITELINKS.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={SITELINKS.RESET_PASSWORD} element={<ResetPassword />} />

        <Route
          path={SITELINKS.DASHBOARD}
          element={
            <ProtectedAdminRoute>
              <Dashboard />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.USERS}
          element={
            <ProtectedAdminRoute>
              <User />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.ELITE_USER}
          element={
            <ProtectedAdminRoute>
              <EliteUser />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={`${SITELINKS.UPDATE_USER}:id`}
          element={
            <ProtectedAdminRoute>
              <UpdateUser />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.CREATE_USER}
          element={
            <ProtectedAdminRoute>
              <CreateProfile />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={`${SITELINKS.USER_PROFILE}:id`}
          element={
            <ProtectedAdminRoute>
              <ShowUser />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.TRANSACTIONS}
          element={
            <ProtectedAdminRoute>
              <Transactions />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.SUBSCRIPTIONS}
          element={
            <ProtectedAdminRoute>
              <Subscriptions />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.BROADCAST}
          element={
            <ProtectedAdminRoute>
              <Broadcast view="new" />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.BROADCAST_HISTORY}
          element={
            <ProtectedAdminRoute>
              <Broadcast view="history" />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.REQUEST}
          element={
            <ProtectedAdminRoute>
              <Request />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.RESPONSES}
          element={
            <ProtectedAdminRoute>
              <Responses />
            </ProtectedAdminRoute>
          }
        />

        {/* account menu pages (placeholders until built) */}
        <Route
          path={SITELINKS.ACCOUNT}
          element={
            <ProtectedAdminRoute>
              <ComingSoon
                title="My account"
                description="You'll be able to view and update your admin profile and photo here."
              />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.CHANGE_PASSWORD}
          element={
            <ProtectedAdminRoute>
              <ComingSoon
                title="Change password"
                description="You'll be able to change your password here without signing out."
              />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.SETTINGS}
          element={
            <ProtectedAdminRoute>
              <ComingSoon
                title="Settings"
                description="Console preferences, such as your default currency and notification choices, will live here."
              />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={SITELINKS.AUDIT_LOG}
          element={
            <ProtectedAdminRoute>
              <ComingSoon
                title="Audit log"
                description="A record of admin actions, such as user status changes, broadcasts and closed requests, will appear here."
              />
            </ProtectedAdminRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default APPRoutes;
